/**
 * Configures the generated @hey-api fetch client.
 *
 * All API calls target `/api/*`. In development, Vite proxies that prefix
 * to the FastAPI backend (see `vite.config.ts`); in production, nginx does
 * the same rewrite (see `nginx.conf`). The browser never knows the backend
 * URL — only the same-origin `/api` path.
 *
 * Importing this module has the side effect of configuring the singleton
 * client. `src/lib/api/index.ts` does this on first import.
 */

import { client } from "@/lib/api/generated/client.gen";
import { logger } from "@/lib/logger";

/**
 * Structured error thrown by all SDK calls.
 *
 * @hey-api/client-fetch with `throwOnError: true` throws just the parsed
 * response body by default, which loses the HTTP status. The error
 * interceptor below wraps every non-OK response in this shape so callers
 * can branch on `status` (e.g. 401 vs 403 vs 400) and read FastAPI's
 * `{ detail: ... }` payload from `body`.
 */
export interface ApiError {
  status: number;
  body: unknown;
}

client.setConfig({
  baseUrl: "/api",
  throwOnError: true,
});

client.interceptors.request.use((request) => {
  logger.debug("[api]", request.method, request.url);
  return request;
});

client.interceptors.response.use((response) => {
  logger.debug("[api]", response.status, response.url);
  return response;
});

client.interceptors.error.use((error, response) => {
  return { status: response.status, body: error } satisfies ApiError;
});

export { client };
